/**
 * NZOS Services Integration.
 *
 * This module provides integration with the NZOS API.
 */

import { logger } from '@accedo/logger';
import { apiCall } from './adapter';

// Export control adapter functions and types to replace Control service
export * from './controlAdapter';

// Logger prefix
const LP = '[NZOSServices]';

/**
 * Initialize NZOS services.
 * Call this during app initialization to set up the service integration.
 */
export async function initializeNZOSServices(): Promise<{
  success: boolean;
  message: string;
}> {
  logger.info(`${LP} Initializing NZOS services...`);

  try {
    logger.info(`${LP} NZOS services initialized successfully`);

    return {
      success: true,
      message: 'NZOS services initialized successfully',
    };
  } catch (error) {
    logger.error(`${LP} Failed to initialize NZOS services:`, error);
    return {
      success: false,
      message: `Initialization failed: ${error}`,
    };
  }
}

// Type alias for API parameters
type ApiParams = Record<string, string | number | boolean>;

/**
 * Simple API helper functions for making calls to NZOS endpoints.
 * These can be used by the existing services or as direct API calls.
 */
export const NZOSApi = {
  /**
   * Get movies from NZOS API.
   */
  async getMovies(params?: ApiParams) {
    logger.debug(`${LP} Fetching movies from NZOS API`);
    return apiCall('/ovp/movies', { params });
  },

  /**
   * Get shows from NZOS API.
   */
  async getShows(params?: ApiParams) {
    logger.debug(`${LP} Fetching shows from NZOS API`);
    return apiCall('/ovp/shows', { params });
  },

  /**
   * Get categories from NZOS API.
   */
  async getCategories(params?: ApiParams) {
    logger.debug(`${LP} Fetching categories from NZOS API`);
    return apiCall('/ovp/categories', { params });
  },

  /**
   * Search content in NZOS API.
   */
  async search(query: string, params?: ApiParams) {
    logger.debug(`${LP} Searching NZOS API for: ${query}`);
    return apiCall('/search', { params: { query, ...params } });
  },

  /**
   * Get configuration from NZOS API.
   */
  async getConfig() {
    logger.debug(`${LP} Fetching config from NZOS API`);
    return apiCall('/config');
  },

  /**
   * Login to NZOS API.
   */
  async login(credentials: { email: string; password: string }) {
    logger.debug(`${LP} Logging in to NZOS API`);
    return apiCall('/auth/login', {
      method: 'POST',
      body: credentials,
    });
  },

  /**
   * Generic API call method.
   */
  apiCall,
};

/**
 * Development utility: Test all major NZOS endpoints.
 */
export async function testNZOSEndpoints(): Promise<{
  success: boolean;
  results: Record<string, { success: boolean; error?: string }>;
}> {
  logger.info(`${LP} Testing NZOS endpoints...`);

  const results: Record<string, { success: boolean; error?: string }> = {};
  let overallSuccess = true;

  // Test endpoints
  const endpoints = [
    { name: 'config', call: () => NZOSApi.getConfig() },
    { name: 'movies', call: () => NZOSApi.getMovies({ pageSize: 1 }) },
    { name: 'shows', call: () => NZOSApi.getShows({ pageSize: 1 }) },
    { name: 'categories', call: () => NZOSApi.getCategories({ pageSize: 1 }) },
  ];

  const testPromises = endpoints.map(async (endpoint) => {
    try {
      await endpoint.call();
      results[endpoint.name] = { success: true };
      logger.debug(`${LP} ✅ ${endpoint.name} endpoint working`);
    } catch (error) {
      results[endpoint.name] = { success: false, error: String(error) };
      logger.error(`${LP} ❌ ${endpoint.name} endpoint failed:`, error);
      overallSuccess = false;
    }
  });

  await Promise.all(testPromises);

  logger.info(`${LP} Endpoint testing complete. Overall success: ${overallSuccess}`);
  return { success: overallSuccess, results };
}

/**
 * Usage Instructions for developers.
 */
export default {
  initializeNZOSServices,
  NZOSApi,
  testNZOSEndpoints,
};
